import { useState, useEffect, useRef, useCallback } from 'react';
import SignaturePad from 'signature_pad';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eraser, Loader2, Type, PenLine, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ClientSignatureModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (signatory_name: string, signature_image: string) => Promise<void>;
  contractContent?: string; // cached_rendered_content (Prosemirror JSON) - optional for signature-only flows
  businessName: string;
  defaultName?: string; // client.billing_name for auto-fill
}

type SignatureMode = 'type' | 'draw';

// Signature font styles - using web-safe cursive fonts + Google Fonts
const SIGNATURE_STYLES = [
  { id: 'style1', fontFamily: "'Dancing Script', cursive", fontSize: 42 },
  { id: 'style2', fontFamily: "'Great Vibes', cursive", fontSize: 42 },
  { id: 'style3', fontFamily: "'Satisfy', cursive", fontSize: 40 },
  { id: 'style4', fontFamily: "'Pacifico', cursive", fontSize: 36 },
] as const;

// Load Google Fonts for signatures
const loadSignatureFonts = () => {
  if (typeof document === 'undefined') return;

  const existingLink = document.getElementById('signature-fonts');
  if (existingLink) return;

  const link = document.createElement('link');
  link.id = 'signature-fonts';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Satisfy&family=Pacifico&display=swap';
  document.head.appendChild(link);
};

export function ClientSignatureModal({
  open,
  onClose,
  onComplete,
  contractContent,
  businessName,
  defaultName = '',
}: ClientSignatureModalProps) {
  const [name, setName] = useState(defaultName);
  const [mode, setMode] = useState<SignatureMode>('type');
  const [selectedStyle, setSelectedStyle] = useState<string>(SIGNATURE_STYLES[0].id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDrawEmpty, setIsDrawEmpty] = useState(true);
  const [showContractDialog, setShowContractDialog] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const typeCanvasRef = useRef<HTMLCanvasElement>(null);

  // Parse and set up the contract content editor
  let parsedContent = null;
  const hasContractContent = !!contractContent;
  if (contractContent) {
    try {
      parsedContent = JSON.parse(contractContent);
    } catch {
      // If parsing fails, content might be plain text
    }
  }

  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: parsedContent || contractContent,
    editable: false,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none',
      },
    },
  });

  // Load fonts on mount
  useEffect(() => {
    loadSignatureFonts();
  }, []);

  // Initialize signature pad when modal opens and mode is 'draw'
  useEffect(() => {
    if (!open || mode !== 'draw') {
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
      return;
    }

    const timeoutId = setTimeout(() => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container || signaturePadRef.current) return;

      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const width = container.offsetWidth || 400;
      canvas.width = width * ratio;
      canvas.height = 160 * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = '160px';

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(ratio, ratio);
        ctx.fillStyle = 'rgb(255, 255, 255)';
        ctx.fillRect(0, 0, width, 160);
      }

      signaturePadRef.current = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 1,
        maxWidth: 2.5,
      });

      signaturePadRef.current.addEventListener('endStroke', () => {
        setIsDrawEmpty(signaturePadRef.current?.isEmpty() ?? true);
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [open, mode]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setName(defaultName);
      setMode('type');
      setSelectedStyle(SIGNATURE_STYLES[0].id);
      setShowContractDialog(false);
    } else {
      setName('');
      setIsDrawEmpty(true);
      setIsSubmitting(false);
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
    }
  }, [open, defaultName]);

  const handleClear = () => {
    signaturePadRef.current?.clear();
    setIsDrawEmpty(true);
  };

  // Generate signature image from typed name
  const generateTypedSignature = useCallback((): string => {
    const canvas = typeCanvasRef.current;
    if (!canvas || !name.trim()) return '';

    const style = SIGNATURE_STYLES.find(s => s.id === selectedStyle) || SIGNATURE_STYLES[0];
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // Set canvas size
    const width = 400;
    const height = 160;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(ratio, ratio);

    // Clear and fill white background
    ctx.fillStyle = 'rgb(255, 255, 255)';
    ctx.fillRect(0, 0, width, height);

    // Draw the signature text
    ctx.fillStyle = 'rgb(0, 0, 0)';
    ctx.font = `${style.fontSize}px ${style.fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name.trim(), width / 2, height / 2);

    return canvas.toDataURL('image/png');
  }, [name, selectedStyle]);

  const handleSign = async () => {
    if (!name.trim()) return;

    let signatureImage: string;

    if (mode === 'type') {
      signatureImage = generateTypedSignature();
      if (!signatureImage) return;
    } else {
      if (isDrawEmpty || !signaturePadRef.current) return;
      signatureImage = signaturePadRef.current.toDataURL('image/png');
    }

    setIsSubmitting(true);
    try {
      await onComplete(name.trim(), signatureImage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = name.trim().length > 0 && (mode === 'type' || !isDrawEmpty) && !isSubmitting;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5" />
            {hasContractContent ? 'Review & Sign Contract' : 'Sign Document'}
          </DialogTitle>
          <DialogDescription>
            {hasContractContent
              ? `Review the contract below and sign to confirm your agreement with ${businessName}`
              : 'Please provide your signature below to acknowledge this document'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Contract link - only show if there's contract content */}
          {hasContractContent && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">By signing, you agree to the</span>
              <button
                type="button"
                onClick={() => setShowContractDialog(true)}
                className="text-primary hover:underline font-medium inline-flex items-center gap-1"
              >
                <FileText className="h-4 w-4" />
                Contract Agreement
              </button>
            </div>
          )}

          {/* Signature Section */}
          <div className="space-y-4">
            {/* Name input */}
            <div className="space-y-2">
              <Label htmlFor="signatory-name">Your Name (Signatory)</Label>
              <Input
                id="signatory-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Type your full name"
                disabled={isSubmitting}
              />
            </div>

            {/* Mode tabs */}
            <div className="flex gap-1 border-b">
              <button
                type="button"
                onClick={() => setMode('type')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                  mode === 'type'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Type className="h-4 w-4" />
                Type
              </button>
              <button
                type="button"
                onClick={() => setMode('draw')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                  mode === 'draw'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <PenLine className="h-4 w-4" />
                Draw
              </button>
            </div>

            {/* Type mode - style selection */}
            {mode === 'type' && (
              <div className="space-y-3">
                <Label>Select Style</Label>
                <div className="grid grid-cols-2 gap-3">
                  {SIGNATURE_STYLES.map((style) => (
                    <button
                      key={style.id}
                      type="button"
                      onClick={() => setSelectedStyle(style.id)}
                      className={cn(
                        'h-16 px-4 border rounded-lg bg-white flex items-center justify-center transition-all',
                        selectedStyle === style.id
                          ? 'border-primary ring-2 ring-primary/20'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <span
                        style={{
                          fontFamily: style.fontFamily,
                          fontSize: `${Math.min(style.fontSize * 0.6, 24)}px`,
                        }}
                        className="text-gray-900 truncate max-w-full"
                      >
                        {name.trim() || 'Your Name'}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Hidden canvas for generating the signature image */}
                <canvas ref={typeCanvasRef} className="hidden" />
              </div>
            )}

            {/* Draw mode - signature pad */}
            {mode === 'draw' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Draw Signature</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClear}
                    disabled={isDrawEmpty || isSubmitting}
                    className="h-7 text-xs text-muted-foreground"
                  >
                    <Eraser className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                </div>
                <div
                  ref={containerRef}
                  className="border rounded-lg bg-white overflow-hidden"
                >
                  <canvas
                    ref={canvasRef}
                    className="w-full touch-none cursor-crosshair"
                    style={{ height: '160px' }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Draw your signature above using your mouse or finger
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <Button onClick={handleSign} disabled={!canSubmit} className="w-full sm:w-auto">
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Signing...
              </>
            ) : (
              'Sign & Continue'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Contract Viewing Dialog - only render if there's contract content */}
      {hasContractContent && (
        <Dialog open={showContractDialog} onOpenChange={setShowContractDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Contract Agreement
              </DialogTitle>
              <DialogDescription>
                Please review the contract terms with {businessName}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto border rounded-lg p-4 bg-white">
              <EditorContent editor={editor} />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
